import { useCallback } from 'react';
import type {
  FloorPlannerLayout,
  LaunchLock,
  RecordLaunchInput,
  SetBiOutcomeInput,
  TwinLaunchSummary,
} from '@repo/frontend-types';
import {
  claimLaunch,
  recordLaunch,
  releaseLaunch,
  setLaunchBiOutcome,
  type ClaimLaunchOutcome,
} from '@repo/front-end-services';
import type { BiOutcomeSave } from './LaunchSuccessSummary';

/**
 * Records a launch in the twin's launch history (spec twin-launch-history-record, R2).
 *
 * ⚠️ ONE RETRY, AND IT IS SAFE. A failed or unanswered call is tried once more with the SAME
 * `launchUuid`; the server returns the row already recorded rather than adding a second, so a first
 * attempt that landed and lost its answer is not doubled.
 *
 * ⚠️ NEVER FAILS THE LAUNCH. The twin is already live when this runs. A launch that could not be
 * recorded is said in the success summary, and the 60-second lock — timed from the record — does
 * not start.
 */
export type LaunchRecordResult =
  | { status: 'recorded'; launch: TwinLaunchSummary; lock: LaunchLock }
  | { status: 'not-recorded'; reason: string; unknown: boolean };

/** What the success summary is told about the record. */
export type SummaryLaunchRecord =
  | { recorded: true }
  | { recorded: false; reason: string; unknown: boolean }
  | null;

export const recordLaunchWithRetry = async (
  middlewareApi: string,
  accessToken: string,
  twinId: number,
  input: RecordLaunchInput,
): Promise<LaunchRecordResult> => {
  let last = await recordLaunch(middlewareApi, accessToken, twinId, input);
  if (last.status !== 'recorded') {
    last = await recordLaunch(middlewareApi, accessToken, twinId, input);
  }
  if (last.status === 'recorded') {
    return { status: 'recorded', launch: last.response.launch, lock: last.response.lock };
  }
  return { status: 'not-recorded', reason: last.error, unknown: last.status === 'unknown' };
};

export const summaryLaunchRecord = (result: LaunchRecordResult | null): SummaryLaunchRecord =>
  result === null
    ? null
    : result.status === 'recorded'
      ? { recorded: true }
      : { recorded: false, reason: result.reason, unknown: result.unknown };

/**
 * Record what the launch's BI step did (spec twin-launch-history-bi-outcome, R3.6).
 *
 * ⚠️ ONE RETRY, AND ONLY WHEN THE ANSWER IS UNKNOWN — no answer, or a gateway error. A definite
 * refusal will not change on a retry (`docs/coding-standards.md`, Retry policy), and an identical
 * replay of one that landed is answered as saved, so the retry cannot double anything.
 *
 * ⚠️ NEVER THROWS, even where the client itself is missing: the twin is live and BI has done what it
 * did, so nothing here may stop the launch's ending. A launch that already has a final result
 * counts as saved: one is on record.
 */
export const saveBiOutcomeWithRetry = async (
  middlewareApi: string,
  accessToken: string,
  twinId: number,
  launchUuid: string,
  input: SetBiOutcomeInput,
): Promise<BiOutcomeSave> => {
  try {
    let result = await setLaunchBiOutcome(middlewareApi, accessToken, twinId, launchUuid, input);
    if (result.status === 'unknown') {
      result = await setLaunchBiOutcome(middlewareApi, accessToken, twinId, launchUuid, input);
    }
    if (result.status === 'saved') return 'saved';
    if (result.status === 'final') {
      console.warn('[go-live] this launch already has a final BI result; this one was not kept', {
        twinId,
        launchUuid,
        outcome: input.outcome,
      });
      return 'saved';
    }
    console.warn('[go-live] BI outcome not saved', {
      twinId,
      launchUuid,
      status: result.status,
      httpStatus: result.httpStatus,
    });
    return result.status === 'unknown' ? 'unknown' : 'not-saved';
  } catch (error) {
    console.warn('[go-live] BI outcome not saved', {
      twinId,
      launchUuid,
      message: error instanceof Error ? error.message : String(error),
    });
    return 'not-saved';
  }
};

/** Everything Go Live knows about the launch it has just made live. */
export interface GoLiveLaunch {
  accessToken: string;
  twinId: number;
  twinBid: string;
  /** Minted when activation succeeded; absent only if a run somehow skipped activation. */
  launchUuid: string | undefined;
  configId: number | null;
  configReused: boolean;
  /** The plan exactly as filed, or null when there is no plan to record. */
  plan: FloorPlannerLayout | null;
  modelCopySuffix: string | null;
  sensorCopySuffix: string | null;
}

export const useLaunchRecord = (middlewareApi: string) => {
  /**
   * Record the launch Go Live has just made live, and say what happened. On success the caller
   * applies the returned lock; on failure the reason is logged here (narrowly — never the client's
   * error object, which carries the bearer token) and goes into the summary.
   */
  const record = useCallback(
    async (launch: GoLiveLaunch): Promise<LaunchRecordResult | null> => {
      if (!launch.plan) return null;
      const result = await recordLaunchWithRetry(middlewareApi, launch.accessToken, launch.twinId, {
        launchUuid: launch.launchUuid ?? crypto.randomUUID(),
        configId: launch.configId,
        configReused: launch.configReused,
        plan: launch.plan,
        modelCopySuffix: launch.modelCopySuffix,
        sensorCopySuffix: launch.sensorCopySuffix,
      });
      if (result.status === 'not-recorded') {
        console.error('[go-live] the twin is live, but the launch was not recorded:', {
          twinBid: launch.twinBid,
          twinId: launch.twinId,
          reason: result.reason,
        });
      }
      return result;
    },
    [middlewareApi],
  );
  /** Claim the twin for a launch, before Go live writes anything. Never throws. */
  const claim = useCallback(
    (args: {
      accessToken: string;
      twinId: number;
      launchUuid: string;
      plan: FloorPlannerLayout;
    }): Promise<ClaimLaunchOutcome> =>
      claimLaunch(middlewareApi, args.accessToken, args.twinId, {
        launchUuid: args.launchUuid,
        plan: args.plan,
      }),
    [middlewareApi],
  );

  /** Give a claim back after a launch stopped before anything went live. Best-effort. */
  const release = useCallback(
    (args: { accessToken: string; twinId: number; launchUuid: string }) =>
      releaseLaunch(middlewareApi, args.accessToken, args.twinId, args.launchUuid),
    [middlewareApi],
  );

  /** Record what the launch's BI step did. Never throws; see `saveBiOutcomeWithRetry`. */
  const saveBiOutcome = useCallback(
    (args: {
      accessToken: string;
      twinId: number;
      launchUuid: string;
      input: SetBiOutcomeInput;
    }): Promise<BiOutcomeSave> =>
      saveBiOutcomeWithRetry(
        middlewareApi,
        args.accessToken,
        args.twinId,
        args.launchUuid,
        args.input,
      ),
    [middlewareApi],
  );

  return { record, claim, release, saveBiOutcome };
};
